/**
 * פתרון נדרש במשימה: השחקן מגיש תמונה ו/או טקסט, והמשימה הבאה נפתחת ישירות רק אחרי שהפתרון אושר
 * (אישור ידני של המנהל, או התאמה אוטומטית לתשובה מוגדרת מראש בפתרונות טקסט).
 */

/** מה מותר להגיש: תמונה בלבד, טקסט בלבד, או כל אחד מהם (או שניהם) */
export type SolutionMode = 'image' | 'text' | 'any';

export type SolutionStatus = 'pending' | 'approved' | 'rejected';

/**
 * בדיקת תמונה אוטומטית (Claude): none — לא נבדקה אוטומטית; checking — בבדיקה עכשיו; done — נבדקה; failed — הבדיקה
 * לא הצליחה (שגיאה/מפתח/מכסה) וההגשה הועברה לאישור ידני של המנהל.
 */
export type AiReviewState = 'none' | 'checking' | 'done' | 'failed';

/** אחרי כמה דחיות אוטומטיות רצופות של אותה משימה ההגשה הבאה עוברת לאישור ידני (מונע תקיעה בגלל טעות של הבדיקה) */
export const AI_REJECTIONS_BEFORE_MANUAL = 3;

/** הגדרת פתרון נדרש למשימה — נשמרת באפליקציה בלבד (לא בגיליון) */
export interface SolutionConfig {
  required: boolean;
  mode: SolutionMode;
  /** הנחיה לשחקן מעל טופס ההגשה (למשל "צלמו קבלה בעברית") */
  prompt: string;
  /** תשובות טקסט שמאושרות אוטומטית; ריק = כל הגשה ממתינה לאישור המנהל */
  answers: string[];
  /** תמונות שמוגשות למשימה הזו נבדקות אוטומטית (כשהבדיקה האוטומטית פעילה); false = תמיד אישור ידני */
  aiCheck: boolean;
}

/** קלט לשמירת הגדרה: aiCheck לא חובה (ברירת מחדל: נבדק אוטומטית) */
export type SolutionConfigInput = Omit<SolutionConfig, 'aiCheck'> & { aiCheck?: boolean };

export const DEFAULT_SOLUTION_CONFIG: SolutionConfig = {
  required: false,
  mode: 'image',
  prompt: '',
  answers: [],
  aiCheck: true,
};

export const SOLUTION_MODE_LABELS: Record<SolutionMode, string> = {
  image: 'תמונה',
  text: 'טקסט',
  any: 'תמונה או טקסט',
};

export const SOLUTION_STATUS_LABELS: Record<SolutionStatus, string> = {
  pending: 'ממתין לבדיקה',
  approved: 'אושר',
  rejected: 'נדחה',
};

export const SOLUTION_TEXT_MAX = 2000;
export const SOLUTION_PROMPT_MAX = 500;
export const SOLUTION_ANSWERS_MAX = 30;
export const SOLUTION_ANSWER_MAX = 200;

/** האם סוג ההגשה מותר לפי מצב המשימה */
export function modeAllowsImage(mode: SolutionMode): boolean {
  return mode === 'image' || mode === 'any';
}

export function modeAllowsText(mode: SolutionMode): boolean {
  return mode === 'text' || mode === 'any';
}

const NIQQUD_AND_MARKS = /[֑-ׇ‎‏‪-‮]/g;
const PUNCTUATION = /[\s"'`´׳״.,;:!?()[\]{}<>\-_–—/\\|*~^%$#@&=+]+/g;

/**
 * מנרמל תשובה להשוואה: בלי ניקוד, בלי סימני כיוון, בלי רווחים/פיסוק, אותיות קטנות, ואותיות סופיות
 * הופכות לרגילות (כך ש"שלום" ו"שלומ" נחשבים זהים — טעות הקלדה נפוצה בטלפון).
 */
export function normalizeAnswer(text: string): string {
  return text
    .normalize('NFKC')
    .replace(NIQQUD_AND_MARKS, '')
    .toLowerCase()
    .replace(PUNCTUATION, '')
    .replace(/ך/g, 'כ')
    .replace(/ם/g, 'מ')
    .replace(/ן/g, 'נ')
    .replace(/ף/g, 'פ')
    .replace(/ץ/g, 'צ');
}

/** האם הטקסט שהוגש תואם אחת מהתשובות המוגדרות (אחרי נרמול). בלי תשובות מוגדרות — אין התאמה אוטומטית */
export function matchesAnswer(text: string, answers: readonly string[]): boolean {
  const given = normalizeAnswer(text);
  if (!given) return false;
  return answers.some((a) => {
    const expected = normalizeAnswer(a);
    return expected !== '' && expected === given;
  });
}

/** ניקוי רשימת תשובות מוגדרות: חיתוך רווחים, הסרת ריקות וכפילויות (אחרי נרמול) והגבלת כמות */
export function cleanAnswers(raw: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const a of raw) {
    const trimmed = a.trim().slice(0, SOLUTION_ANSWER_MAX);
    const key = normalizeAnswer(trimmed);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
    if (out.length >= SOLUTION_ANSWERS_MAX) break;
  }
  return out;
}

export type SubmissionCheck =
  | { ok: true }
  | { ok: false; reason: string };

/** בדיקת תקינות הגשה מול מצב המשימה (סוג מותר, לא ריק) */
export function checkSubmission(
  mode: SolutionMode,
  text: string,
  imageUrl: string | null,
): SubmissionCheck {
  const hasText = text.trim() !== '';
  const hasImage = Boolean(imageUrl);
  if (!hasText && !hasImage) return { ok: false, reason: 'ההגשה ריקה — הוסיפו תמונה או טקסט' };
  if (hasImage && !modeAllowsImage(mode)) {
    return { ok: false, reason: 'במשימה הזו מגישים טקסט בלבד' };
  }
  if (hasText && !modeAllowsText(mode)) {
    return { ok: false, reason: 'במשימה הזו מגישים תמונה בלבד' };
  }
  if (mode === 'image' && !hasImage) return { ok: false, reason: 'צריך לצרף תמונה' };
  if (mode === 'text' && !hasText) return { ok: false, reason: 'צריך לכתוב תשובה' };
  return { ok: true };
}
